import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { deleteApi } from '../../api/deletes'
import { Alert } from '../../components/Alert'
import { BbIcon } from '../../components/BbIcon'
import { BbStepper } from '../../components/BbStepper'
import { useCompanies } from '../../companies/useCompanies'
import { useDeleteWizard } from '../../delete/DeleteWizardContext'
import { formatInt } from '../../lib/format'
import { DELETE_STEPS } from './steps'

/* Step 4, ported from "batchbooks_delete_final_confirmation_synced".

   The submit lives in this confirm button, not in an effect on the done page:
   an effect double-fires under React StrictMode (which is on), so it used to
   queue two identical delete jobs per click. One click = one job, and the button
   is disabled while the request is in flight so a double-click cannot either.

   The ack checkbox genuinely gates the button — this is the last screen before a
   destructive action. */
export function DeleteConfirmPage() {
  const wizard = useDeleteWizard()
  const navigate = useNavigate()
  const { current } = useCompanies()
  const [acked, setAcked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!wizard.entityType) return <Navigate to="/app/delete" replace />

  const subject = wizard.entity?.label ?? wizard.entityType
  const count = wizard.review?.length ?? (wizard.source === 'refs' ? wizard.refList.length : null)
  const companyId = current?.id ?? null

  const confirm = async () => {
    if (!companyId) {
      setError('No QuickBooks company is connected.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const { jobId } = await deleteApi.submit({
        qboCompanyId: companyId,
        entityType: wizard.entityType,
        source: wizard.source,
        dateRange: wizard.source === 'filter' ? wizard.dateRange : undefined,
        from: wizard.source === 'filter' ? wizard.from || undefined : undefined,
        to: wizard.source === 'filter' ? wizard.to || undefined : undefined,
        amountMin: wizard.source === 'filter' ? wizard.amountMin || undefined : undefined,
        amountMax: wizard.source === 'filter' ? wizard.amountMax || undefined : undefined,
        references: wizard.source === 'refs' ? wizard.refList : undefined,
        // The records the user kept on the review step — only these are deleted,
        // not the whole date range that produced them.
        targetIds: wizard.review?.map((item) => item.qboId),
      })
      // Hand the new job to the done page so it shows the real job id.
      navigate('/app/delete/done', { state: { jobId } })
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.')
      setSubmitting(false)
    }
  }

  return (
    <div className="bb bb-wizard">
      <BbStepper steps={DELETE_STEPS} current={3} />

      {error ? (
        <Alert tone="err" title="Nothing has been deleted" style={{ marginBottom: 18 }}>
          {error}
        </Alert>
      ) : null}

      <div className="bb-panel-card">
        <div className="bb-panel-card-b">
          <div className="bb-danger">
            <span className="bb-danger-ico">
              <BbIcon name="warning" />
            </span>
            <h2>Are you absolutely sure?</h2>
            <p className="bb-danger-sub">Confirm Deletion</p>
            <p>
              You are about to delete{' '}
              <b>
                {count === null ? 'the selected' : formatInt(count)} {subject}
                {count === 1 ? '' : 's'}
              </b>
              . This would permanently remove them from{' '}
              {current?.companyName ?? 'your QuickBooks Online company'}.
            </p>

            <div className="bb-ack">
              <input
                className="bb-check"
                id="ack"
                type="checkbox"
                checked={acked}
                onChange={(event) => setAcked(event.target.checked)}
              />
              <label htmlFor="ack">
                I understand that this action cannot be undone and these records cannot be
                recovered.
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="bb-wizard-foot">
        <Link className="bb-btn bb-btn-quiet" to="/app/delete/review">
          <BbIcon name="arrow-left" />
          Back
        </Link>
        <span className="bb-wizard-foot-r">
          <button
            className="bb-btn bb-btn-danger"
            type="button"
            disabled={!acked || submitting}
            onClick={() => void confirm()}
          >
            {submitting ? 'Submitting…' : 'Confirm & Delete Now'}
          </button>
        </span>
      </div>
    </div>
  )
}
